import * as React from 'react';

// max slice size to render at a time
const kSliceSize: number = 20;

interface ScrollableListProps<T> {
    renderItemFunc: (item: T, index: number) => React.ReactNode;
    items: T[];
    className?: string;
}

interface ScrollableListState<T> {
    allItems: T[];
    displayedItems: T[];
}

class ScrollableList<T> extends React.Component<ScrollableListProps<T>, ScrollableListState<T>> {
    private _animationFrameRequest: Nullable<number>;

    constructor(props: ScrollableListProps<T>) {
        super(props);
        this.state = Object.assign({}, this.state, this._stateFromProps(props));
        this._animationFrameRequest = props.items.length > kSliceSize ?
            window.requestAnimationFrame(() => this._renderNextSlice()) :
            null;
    }

    componentWillUnmount(): void {
        // clean up any pending request
        if (this._animationFrameRequest) {
            window.cancelAnimationFrame(this._animationFrameRequest);
            this._animationFrameRequest = null;
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: ScrollableListProps<T>): void {
        if (!nextProps.items || this._itemsEqual(nextProps.items, this.props.items)) {
            return;
        }

        // cancel pending request
        if (this._animationFrameRequest) {
            window.cancelAnimationFrame(this._animationFrameRequest);
            this._animationFrameRequest = null;
        }

        // assume that if we're receiving new props, we want to re-render all items
        this.setState(this._stateFromProps(nextProps));

        // if we have more items to render, kick off timer
        if (nextProps.items.length > kSliceSize) {
            this._animationFrameRequest = window.requestAnimationFrame(() => this._renderNextSlice());
        }
    }

    render(): React.ReactNode {
        const { className, renderItemFunc } = this.props;
        const { displayedItems } = this.state;

        return (
            <ul className={`scrollable-list ${className || ''}`}>
                { displayedItems && renderItemFunc && displayedItems.map(renderItemFunc) }
            </ul>
        );
    }

    private _itemsEqual(oldItems: T[], newItems: T[]): boolean {
        if (oldItems !== newItems || oldItems.length !== newItems.length) {
            return false;
        }

        const length = newItems.length;
        for (let i = 0; i < length; i++) {
            if (oldItems[i] !== newItems[i]) {
                return false;
            }
        }

        return true;
    }

    private _stateFromProps(newProps: ScrollableListProps<T>): ScrollableListState<T> {
        return {
            allItems: newProps ? newProps.items : [],
            displayedItems: newProps ? newProps.items.slice(0, kSliceSize) : []
        };
    }

    private _renderNextSlice(): void {
        const { allItems, displayedItems } = this.state;
        const delta = allItems.length - displayedItems.length;
        this._animationFrameRequest = null;
        if (delta <= 0) {
            // shouldn't happen, but let's be defensive
            return;
        }

        this.setState({
            displayedItems: allItems.slice(0, displayedItems.length + kSliceSize)
        });

        if (delta > kSliceSize) {
            this._animationFrameRequest = window.requestAnimationFrame(() => this._renderNextSlice());
        }
    }
}

export { ScrollableList };
export default ScrollableList;